/**
 * Pure header status mapping for the call window, extracted from
 * `CallWindow.tsx` so it unit-tests directly. Maps the broker call state +
 * the live ICE connection state to the header dot's label + colour.
 *
 * While connected, the ICE state folds in: a healthy/unknown path reads
 * "Connected" (green), a `failed` path reads "Connection lost" (red), and any
 * other transient state reads "Reconnecting…" (amber).
 */
export interface CallStatusDisplay {
  label: string;
  /** Literal colour token (raw values match the dark in-call chrome). */
  color: string;
}

export function callStatus(
  state: string | undefined,
  connectionState: RTCIceConnectionState | null,
): CallStatusDisplay {
  const isRinging = state === "ringing";
  const isEnded = state === "ended";
  const isConnected = state === "connected";

  const iceHealthy =
    connectionState === "connected" || connectionState === "completed" || connectionState === null;
  const iceFailed = connectionState === "failed";

  const label = isRinging
    ? "Ringing…"
    : isEnded
      ? "Ended"
      : isConnected
        ? iceFailed
          ? "Connection lost"
          : iceHealthy
            ? "Connected"
            : "Reconnecting…"
        : "Connecting…";

  const color = isEnded
    ? "rgba(255,255,255,0.35)"
    : isConnected && iceHealthy
      ? "#3ddc84" // green
      : isConnected && iceFailed
        ? "#ff5252" // red
        : "#ffb300"; // amber (ringing / connecting / reconnecting)

  return { label, color };
}
